// Interactive balloon emoji field laid out by Pretext, with mouse/touch repulsion physics.

import React, { useEffect, useRef } from 'react';
import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext';
import Logo from '@/components/navigation/Logo';

type BalloonFieldProps = {
  /** Decorative mode: no Logo/fog/pulse, transparent canvas, window-level
   *  event listeners, no touch preventDefault, no cursor hiding. Lets the
   *  field sit as a background layer beneath other interactive content. */
  decorative?: boolean;
  /** Retain the canvas and physics state while suspending redraws. */
  paused?: boolean;
};

const EMOJI = '🎈';
const LOGO_MASK_WIDTH = 520;
const LOGO_MASK_HEIGHT = 110;
const FONT_SIZE = 40;
const FONT = `${FONT_SIZE}px Inter, sans-serif`;
const LINE_HEIGHT = FONT_SIZE + 4;
const REPEL_RADIUS = 140;
const REPEL_RADIUS_SQ = REPEL_RADIUS * REPEL_RADIUS;
const REPEL_STRENGTH = 100;
const SPRING = 0.08;
const DAMPING = 0.82;
const SETTLE_THRESHOLD = 0.5;
const PULSE_COLOR = [239, 68, 68] as const;

type Glyph = {
  homeX: number;
  homeY: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  vr: number;
};

const BalloonField: React.FC<BalloonFieldProps> = ({
  decorative = false,
  paused = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<{ start: () => void; stop: () => void } | null>(
    null
  );
  const stateRef = useRef<{
    glyphs: Glyph[];
    mouseX: number;
    mouseY: number;
    mouseActive: boolean;
    revealActive: boolean;
    lastPointerTime: number;
    canvasW: number;
    canvasH: number;
  }>({
    glyphs: [],
    mouseX: -9999,
    mouseY: -9999,
    mouseActive: false,
    revealActive: false,
    lastPointerTime: 0,
    canvasW: 0,
    canvasH: 0,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    // Measure single emoji width for character offsets within each line
    ctx.font = FONT;
    const charWidth = ctx.measureText(EMOJI).width;

    function sizeCanvas() {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas!.getBoundingClientRect();
      canvas!.width = rect.width * dpr;
      canvas!.height = rect.height * dpr;
      stateRef.current.canvasW = rect.width;
      stateRef.current.canvasH = rect.height;
    }

    function buildLayout() {
      sizeCanvas();
      const { canvasW: w, canvasH: h } = stateRef.current;

      // Build a string of emojis long enough to fill the screen.
      // Pretext wraps this into lines for us — that's the layout engine.
      const charsPerLine = Math.floor(w / charWidth);
      const lineCount = Math.ceil(h / LINE_HEIGHT);
      const totalChars = charsPerLine * (lineCount + 2);
      const text = EMOJI.repeat(totalChars);

      const prepared = prepareWithSegments(text, FONT);
      const result = layoutWithLines(prepared, w, LINE_HEIGHT);

      // Each character in each Pretext-laid-out line becomes a physics glyph
      // Center each row: offset by half the leftover space
      const glyphs: Glyph[] = [];
      const totalLines = result.lines.length;
      const totalHeight = totalLines * LINE_HEIGHT;
      const offsetY = (h - totalHeight) / 2;
      for (let i = 0; i < result.lines.length; i++) {
        const line = result.lines[i];
        const count = [...line.text].length;
        const rowWidth = count * charWidth;
        const offsetX = (w - rowWidth) / 2;
        for (let c = 0; c < count; c++) {
          const hx = offsetX + c * charWidth + charWidth / 2;
          const hy = offsetY + i * LINE_HEIGHT + LINE_HEIGHT / 2;
          glyphs.push({
            homeX: hx,
            homeY: hy,
            x: hx,
            y: hy,
            vx: 0,
            vy: 0,
            rotation: 0,
            vr: 0,
          });
        }
      }
      stateRef.current.glyphs = glyphs;
    }

    buildLayout();

    const onResize = () => buildLayout();
    window.addEventListener('resize', onResize);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onResize)
        : null;
    resizeObserver?.observe(canvas);

    function setPointer(clientX: number, clientY: number) {
      const rect = canvas!.getBoundingClientRect();
      stateRef.current.mouseX = clientX - rect.left;
      stateRef.current.mouseY = clientY - rect.top;
      stateRef.current.mouseActive = true;
      stateRef.current.revealActive = true;
      stateRef.current.lastPointerTime = performance.now();
    }

    const onMouseMove = (e: MouseEvent) => setPointer(e.clientX, e.clientY);
    const onMouseLeave = () => {
      stateRef.current.mouseActive = false;
      stateRef.current.revealActive = false;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!decorative) e.preventDefault();
      setPointer(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!decorative) e.preventDefault();
      setPointer(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd = () => {
      stateRef.current.mouseActive = false;
      stateRef.current.revealActive = false;
    };

    const eventTarget: HTMLElement | Window = decorative ? window : canvas;
    eventTarget.addEventListener('mousemove', onMouseMove as EventListener);
    eventTarget.addEventListener('mouseleave', onMouseLeave as EventListener);
    eventTarget.addEventListener('touchstart', onTouchStart as EventListener, {
      passive: decorative,
    });
    eventTarget.addEventListener('touchmove', onTouchMove as EventListener, {
      passive: decorative,
    });
    eventTarget.addEventListener('touchend', onTouchEnd as EventListener);

    let animId: number | null = null;
    let running = false;

    function tick() {
      if (!running) return;
      const dpr = window.devicePixelRatio || 1;
      const {
        glyphs,
        mouseX,
        mouseY,
        mouseActive,
        lastPointerTime,
        canvasW: w,
        canvasH: h,
      } = stateRef.current;

      const nowMs = performance.now();
      const now = nowMs / 1000;
      const cx = w / 2;
      const cy = h / 2;

      // Reset transform then clear
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (decorative) {
        ctx.clearRect(0, 0, w, h);
      } else {
        ctx.fillStyle = '#0f0f14';
        ctx.fillRect(0, 0, w, h);
      }

      // --- Physics ---
      for (let i = 0; i < glyphs.length; i++) {
        const g = glyphs[i];
        g.vx = (g.vx + (g.homeX - g.x) * SPRING) * DAMPING;
        g.vy = (g.vy + (g.homeY - g.y) * SPRING) * DAMPING;

        if (mouseActive) {
          const mx = g.x - mouseX;
          const my = g.y - mouseY;
          const distSq = mx * mx + my * my;
          if (distSq < REPEL_RADIUS_SQ && distSq > 0.01) {
            const dist = Math.sqrt(distSq);
            const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH * 0.15;
            const nx = mx / dist;
            const ny = my / dist;
            g.vx += nx * force;
            g.vy += ny * force;
            g.vr += (nx * my - ny * mx) * 0.0004 * force;
          }
        }

        g.x += g.vx;
        g.y += g.vy;
        g.vr = (g.vr - g.rotation * 0.06) * 0.88;
        g.rotation += g.vr;
      }

      // --- Logo fog (fog only covers the centered logo bounds, not the whole screen) ---
      if (!decorative) {
        const logoMaskW = Math.min(LOGO_MASK_WIDTH, w - 40);
        const logoMaskH = Math.min(LOGO_MASK_HEIGHT, h - 40);
        const logoMaskX = cx - logoMaskW / 2;
        const logoMaskY = cy - logoMaskH / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(logoMaskX, logoMaskY, logoMaskW, logoMaskH);
        ctx.clip();
        const { revealActive } = stateRef.current;
        if (revealActive) {
          const revealRadius = REPEL_RADIUS * 1.2;
          const fog = ctx.createRadialGradient(
            mouseX,
            mouseY,
            0,
            mouseX,
            mouseY,
            revealRadius
          );
          fog.addColorStop(0, 'rgba(15,15,20,0)');
          fog.addColorStop(0.7, 'rgba(15,15,20,0.6)');
          fog.addColorStop(1, 'rgba(15,15,20,1)');
          ctx.fillStyle = fog;
        } else {
          ctx.fillStyle = '#0f0f14';
        }
        ctx.fillRect(logoMaskX, logoMaskY, logoMaskW, logoMaskH);
        ctx.restore();
      }

      // --- Draw balloons ---
      ctx.font = FONT;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (let i = 0; i < glyphs.length; i++) {
        const g = glyphs[i];
        const dx = g.x - g.homeX;
        const dy = g.y - g.homeY;
        const dispSq = dx * dx + dy * dy;

        if (dispSq < SETTLE_THRESHOLD) {
          ctx.fillText(EMOJI, g.homeX, g.homeY);
        } else {
          ctx.save();
          ctx.translate(g.x, g.y);
          if (Math.abs(g.rotation) > 0.005) ctx.rotate(g.rotation);
          const s = 1 + Math.min(Math.sqrt(dispSq) / 200, 0.35);
          if (s > 1.01) ctx.scale(s, s);
          ctx.fillText(EMOJI, 0, 0);
          ctx.restore();
        }
      }

      // --- Idle pulse ---
      if (!decorative) {
        const idleMs = nowMs - lastPointerTime;
        const idleFade =
          lastPointerTime === 0
            ? 1
            : Math.min(Math.max((idleMs - 1500) / 1500, 0), 1);
        if (idleFade > 0) {
          const pulse = (Math.sin(now * 1.3) + 1) / 2;
          const [r, g, b] = PULSE_COLOR;
          const alpha = (0.3 + pulse * 0.7) * idleFade;
          const radius = 180 + pulse * 120;
          const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
          grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
          grad.addColorStop(0.4, `rgba(${r},${g},${b},${alpha * 0.3})`);
          grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, w, h);
        }
      }

      animId = requestAnimationFrame(tick);
    }

    const animation = {
      start() {
        if (running) return;
        running = true;
        animId = requestAnimationFrame(tick);
      },
      stop() {
        running = false;
        if (animId !== null) cancelAnimationFrame(animId);
        animId = null;
      },
    };
    animationRef.current = animation;

    return () => {
      animation.stop();
      animationRef.current = null;
      window.removeEventListener('resize', onResize);
      resizeObserver?.disconnect();
      eventTarget.removeEventListener(
        'mousemove',
        onMouseMove as EventListener
      );
      eventTarget.removeEventListener(
        'mouseleave',
        onMouseLeave as EventListener
      );
      eventTarget.removeEventListener(
        'touchstart',
        onTouchStart as EventListener
      );
      eventTarget.removeEventListener(
        'touchmove',
        onTouchMove as EventListener
      );
      eventTarget.removeEventListener('touchend', onTouchEnd as EventListener);
    };
  }, [decorative]);

  useEffect(() => {
    const animation = animationRef.current;
    if (!paused) animation?.start();
    return () => animation?.stop();
  }, [paused, decorative]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!decorative && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Logo className="pointer-events-none select-none" beatTint />
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: decorative ? undefined : 'none',
          touchAction: decorative ? undefined : 'none',
        }}
      />
    </div>
  );
};

export default BalloonField;
